import { useEffect, useMemo, useState } from 'react';
import type { Task } from '../../types/crm';
import { useCrmData } from '../../contexts/CrmDataContext';
import {
  buildTaskTitlePieSlices,
  formatTaskMonthLabel,
  listTaskMonthOptions,
  pickDefaultTaskChartMonth,
  taskDueMonthKey,
} from '../../utils/taskHelpers';
import { TaskTitlePieChart } from '../tasks/TaskTitlePieChart';

type MonthOption = {
  value: string;
  label: string;
};

type DashboardTaskTitlePieProps = {
  embedded?: boolean;
  /** Controlled month — used by dashboard tabs so the Tasks tab label stays in sync. */
  chartMonth?: string;
  onMonthChange?: (value: string) => void;
  monthOptions?: MonthOption[];
  scopeTasks?: Task[];
  /** When true, month select is rendered by the parent (e.g. panel title row). */
  hideMonthSelect?: boolean;
};

export function DashboardTaskTitlePie({
  embedded = false,
  chartMonth: controlledMonth,
  onMonthChange: controlledOnMonthChange,
  monthOptions: controlledMonthOptions,
  scopeTasks: controlledScopeTasks,
  hideMonthSelect = false,
}: DashboardTaskTitlePieProps) {
  const { getMyData } = useCrmData();
  const [internalMonth, setInternalMonth] = useState('');

  const isControlled = controlledMonth !== undefined && controlledOnMonthChange != null;

  const scopeTasks = useMemo(
    () => controlledScopeTasks ?? [...getMyData('tasks')],
    [controlledScopeTasks, getMyData],
  );
  const monthOptions = useMemo(
    () => controlledMonthOptions ?? listTaskMonthOptions(scopeTasks),
    [controlledMonthOptions, scopeTasks],
  );

  const chartMonth = isControlled ? controlledMonth : internalMonth;
  const setChartMonth = isControlled ? controlledOnMonthChange : setInternalMonth;

  useEffect(() => {
    if (isControlled) return;
    if (monthOptions.length === 0) {
      setInternalMonth('');
      return;
    }
    if (internalMonth && monthOptions.some((option) => option.value === internalMonth)) return;
    const preferred = pickDefaultTaskChartMonth(monthOptions, scopeTasks);
    if (preferred) setInternalMonth(preferred);
  }, [isControlled, monthOptions, internalMonth, scopeTasks]);

  const chartSlices = useMemo(
    () => (chartMonth ? buildTaskTitlePieSlices(scopeTasks, chartMonth) : []),
    [scopeTasks, chartMonth],
  );

  const chartTotal = useMemo(
    () => (chartMonth ? scopeTasks.filter((task) => taskDueMonthKey(task) === chartMonth).length : 0),
    [scopeTasks, chartMonth],
  );

  return (
    <div className={`dashboard-task-pie${embedded ? ' dashboard-task-pie--embedded' : ''}`}>
      <TaskTitlePieChart
        monthLabel={chartMonth ? formatTaskMonthLabel(chartMonth) : '—'}
        total={chartTotal}
        slices={chartSlices}
        monthOptions={hideMonthSelect ? undefined : monthOptions}
        chartMonth={chartMonth}
        onMonthChange={hideMonthSelect ? undefined : setChartMonth}
        embedded={embedded}
        hideSubtitle={embedded}
      />
    </div>
  );
}
